import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, downloadBlob } from "../../lib/api";
import { eur } from "../../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Send, Download } from "lucide-react";

const CUST_FIELDS = [
  ["customer_name", "Nombre y apellidos"], ["company", "Empresa"], ["cif", "CIF / NIF"], ["email", "Email"],
  ["address", "Dirección"], ["postal_code", "Código postal"], ["city", "Población"], ["province", "Provincia"],
];

export default function AdminInvoiceForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    adminApi.get(`/admin/invoices/${id}`).then((r) => {
      setInv(r.data);
      setItems((r.data.items || []).map((it) => ({ ...it, price: Number(it.price), quantity: Number(it.quantity) })));
      setDiscount(Number(r.data.discount) || 0);
    }).catch(() => toast.error("Factura no encontrada"));
  }, [id]);

  if (!inv) return <div className="h-40 bg-secondary animate-pulse rounded-sm" />;

  const set = (k, v) => setInv((x) => ({ ...x, [k]: v }));
  const updateItem = (idx, k, v) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const subtotal = items.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
  const base = Math.max(subtotal - (Number(discount) || 0), 0);
  const iva = base * (Number(inv.iva_rate) || 0.21);

  const save = async () => {
    const clean = items.filter((it) => String(it.name || "").trim() && Number(it.quantity) > 0);
    if (clean.length === 0) { toast.error("La factura debe tener al menos una línea"); return; }
    setSaving(true);
    try {
      const payload = { date: inv.date, observations: inv.observations || "", items: clean, discount: Number(discount) || 0 };
      CUST_FIELDS.forEach(([k]) => { payload[k] = inv[k] || ""; });
      const r = await adminApi.put(`/admin/invoices/${id}`, payload);
      setInv((x) => ({ ...x, ...r.data }));
      toast.success("Factura actualizada");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar la factura");
      return false;
    } finally { setSaving(false); }
  };

  const resend = async () => {
    setSending(true);
    try {
      const ok = await save();
      if (ok === false) return;
      const r = await adminApi.post(`/admin/invoices/${id}/resend`);
      toast.success(`Factura reenviada a ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reenviar la factura");
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="invoice-form">
      <button onClick={() => nav("/admin/facturas")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand mb-4"><ArrowLeft className="h-4 w-4" /> Volver a facturas</button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading font-bold text-3xl tracking-tighter">Editar factura <span className="font-mono text-brand">{inv.number}</span></h1>
        <span className="text-sm text-muted-foreground">Pedido {inv.order_number}{inv.resent_at && ` · Reenviada ${new Date(inv.resent_at).toLocaleString("es-ES")}`}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="font-heading font-semibold mb-3">Datos del cliente</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {CUST_FIELDS.map(([k, l]) => (
                <label key={k} className="text-xs text-muted-foreground">{l}
                  <input value={inv[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-0.5 w-full px-3 py-2 text-sm text-foreground border border-border rounded-sm" data-testid={`inv-${k}`} />
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading font-semibold">Líneas de la factura</p>
              <button onClick={() => setItems((p) => [...p, { product_id: "", name: "", reference: "", price: 0, quantity: 1 }])} className="inline-flex items-center gap-1 border border-border rounded-sm px-3 py-1 text-sm hover:border-brand" data-testid="inv-add-line"><Plus className="h-4 w-4" /> Añadir línea</button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Concepto</th><th className="w-20 text-center">Cant.</th><th className="w-28 text-right">Precio</th><th className="w-24 text-right">Subtotal</th><th className="w-8"></th></tr></thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className="border-t border-border" data-testid={`inv-line-${idx}`}>
                    <td className="py-1.5 pr-2">
                      <input value={it.name} onChange={(e) => updateItem(idx, "name", e.target.value)} className="w-full px-2 py-1 text-sm border border-border rounded-sm" data-testid={`inv-line-name-${idx}`} />
                      <input value={it.reference || ""} onChange={(e) => updateItem(idx, "reference", e.target.value)} placeholder="Ref." className="w-full px-2 py-0.5 mt-1 text-xs border border-border rounded-sm font-mono" />
                    </td>
                    <td className="text-center"><input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="w-16 px-2 py-1 text-sm border border-border rounded-sm text-center" data-testid={`inv-line-qty-${idx}`} /></td>
                    <td className="text-right"><input type="number" step="0.01" min="0" value={it.price} onChange={(e) => updateItem(idx, "price", e.target.value)} className="w-24 px-2 py-1 text-sm border border-border rounded-sm text-right" data-testid={`inv-line-price-${idx}`} /></td>
                    <td className="text-right font-medium">{eur((Number(it.price) || 0) * (Number(it.quantity) || 0))}</td>
                    <td className="text-right"><button onClick={() => setItems((p) => p.filter((_, i) => i !== idx))} className="p-1 text-red-500 hover:text-red-700" data-testid={`inv-remove-line-${idx}`}><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-border rounded-sm p-4">
            <label className="text-sm font-medium block mb-1">Observaciones</label>
            <textarea value={inv.observations || ""} onChange={(e) => set("observations", e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="inv-observations" />
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-border rounded-sm p-4 space-y-3">
            <label className="text-sm font-medium block">Fecha de factura
              <input value={inv.date || ""} onChange={(e) => set("date", e.target.value)} placeholder="dd/mm/aaaa" className="mt-1 w-full px-3 py-2 text-sm font-normal border border-border rounded-sm" data-testid="inv-date" />
            </label>
            <label className="text-sm font-medium block">Descuento (€)
              <input type="number" step="0.01" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm font-normal border border-border rounded-sm" data-testid="inv-discount" />
            </label>
          </div>
          <div className="bg-white border border-border rounded-sm p-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{eur(subtotal)}</span></div>
            {Number(discount) > 0 && <div className="flex justify-between text-emerald-700"><span>Descuento</span><span className="font-mono">−{eur(Number(discount))}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">IVA ({Math.round((Number(inv.iva_rate) || 0.21) * 100)}%)</span><span className="font-mono">{eur(iva)}</span></div>
            <div className="flex justify-between font-bold pt-1"><span>Total</span><span className="text-brand" data-testid="inv-total">{eur(base + iva)}</span></div>
          </div>
          <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white py-3 rounded-sm font-semibold hover:bg-brand-dark disabled:opacity-60" data-testid="save-invoice-btn"><Save className="h-4 w-4" /> {saving ? "Guardando..." : "Guardar cambios"}</button>
          <button onClick={resend} disabled={sending || saving} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white py-3 rounded-sm font-semibold hover:bg-emerald-700 disabled:opacity-60" data-testid="resend-invoice-btn"><Send className="h-4 w-4" /> {sending ? "Enviando..." : "Guardar y reenviar al cliente"}</button>
          <button onClick={() => downloadBlob(`/admin/invoices/${id}/pdf`, `${inv.number}.pdf`)} className="w-full inline-flex items-center justify-center gap-2 border border-border py-2.5 rounded-sm text-sm hover:border-brand" data-testid="invoice-form-pdf-btn"><Download className="h-4 w-4" /> Descargar PDF</button>
          <p className="text-xs text-muted-foreground">El reenvío envía la factura en PDF al email del cliente indicado arriba y copia a administración.</p>
        </div>
      </div>
    </div>
  );
}
